import { ProductDataOutput } from "../helpers/productHelper";
import { localImages } from "../../components/shared/localImages";

export const getImageUrl = (product: any): string => {
  if (!product?.name) return "";

  const nameLower = product.name.toLowerCase();
  const productTypeName = product.productTypeName?.toLowerCase() || "";
  const typeWithOutMarket = product.typeWithOutMarket?.toLowerCase() || "";
  const type = product.type?.value?.toLowerCase() || "";
  
  // 🔹 Kolla buckets FÖRST (högsta prioritet) för att undvika felaktiga matchningar
  // En bucket kan identifieras genom productTypeName, namnet, eller typeWithOutMarket
  // Notera: GB1500, GB2000 etc (Grading Beams) har inget mellanslag, buckets har "GB 750 l"
  const isBucket = productTypeName.includes("bucket") || 
                   productTypeName.includes("skopa") ||
                   nameLower.includes("bucket") || 
                   nameLower.includes("skopa") ||
                   /^(db|tb|sb)\s/.test(nameLower) ||
                   (/^gb\s/.test(nameLower) && nameLower.includes(" l")) ||
                   typeWithOutMarket === "utility" ||
                   typeWithOutMarket === "digger" ||
                   typeWithOutMarket === "cable" ||
                   typeWithOutMarket === "sorting";
  
  if (isBucket) {
    // Kolla typeWithOutMarket först (mest exakt)
    if (typeWithOutMarket === "utility" || type === "utility") {
      return localImages.GradingBucket;
    }
    
    if (typeWithOutMarket === "digger" || type === "digger") {
      // Kolla toothType för att avgöra om det är teeth eller edge
      if (product.toothType || nameLower.includes("teeth") || nameLower.includes("tänder") || nameLower.includes("_t")) {
        return localImages.ExcavationBucketTeeth;
      }
      return localImages.ExcavationBucket;
    }
    
    if (typeWithOutMarket === "cable" || type === "cable") {
      return localImages.TrenchingBucket;
    }
    
    if (typeWithOutMarket === "sorting" || type === "sorting") {
      return localImages.SortingBucket;
    }

    // Fallback för buckets baserat på namn om type saknas
    // Excavation/Digging buckets (DB = Digging Bucket)
    if (nameLower.includes("excavation") || nameLower.includes("digging") || nameLower.includes("gravskopa") || nameLower.startsWith("db ")) {
      if (nameLower.includes("teeth") || nameLower.includes("tänder") || nameLower.includes("_t")) {
        return localImages.ExcavationBucketTeeth;
      }
      return localImages.ExcavationBucket;
    }

    // Trenching/Cable buckets (TB = Trenching Bucket) - måste matcha FÖRE Grading eftersom "trenching" är mer specifikt
    if (nameLower.includes("trenching") || nameLower.includes("cable") || nameLower.includes("kabel") || nameLower.startsWith("tb ")) {
      return localImages.TrenchingBucket;
    }

    // Grading/Universal buckets (GB = Grading Bucket/Universal bucket)
    if (nameLower.includes("grading") || nameLower.includes("universal") || nameLower.includes("avjämning") || nameLower.startsWith("gb ")) {
      return localImages.GradingBucket;
    }

    // Sorting buckets (SB = Sorting Bucket)
    if (nameLower.includes("sorting") || nameLower.includes("sortering") || nameLower.startsWith("sb ")) {
      return localImages.SortingBucket;
    }

    // Om det är en bucket men ingen specifik match, försök gissa baserat på typ
    // Fallback för buckets
    return localImages.GradingBucket;
  }
  
  // 🔹 Nu är vi säkra på att det INTE är en bucket, kolla andra produkttyper
  if (productTypeName.includes("compactor") || productTypeName.includes("markvibrator")) {
    return localImages.compactor;
  }

  if (productTypeName.includes("grading beam") || productTypeName.includes("avjämningsbalk") || /^gb\d+$/i.test(nameLower)) {
    return localImages.GB;
  }

  if (productTypeName.includes("ripper") || productTypeName.includes("rivtand")) {
    return localImages.ripper;
  }

  if (productTypeName.includes("asphalt") || productTypeName.includes("asfalt")) {
    return localImages.asphaltCutter;
  }

  if (productTypeName.includes("railroad") || productTypeName.includes("järnväg")) {
    return localImages.jarnvagsgrip;
  }

  if (productTypeName.includes("grapple module") || productTypeName.includes("gripmodul")) {
    return localImages.RG;
  }

  // 🔹 Railroad grapple måste kollas FÖRE tiltrotatorer (RR820 skulle annars matcha R820)
  if (nameLower.startsWith("rr") || productTypeName.includes("railroad") || productTypeName.includes("järnväg")) {
    return localImages.jarnvagsgrip;
  }

  // 🔹 Grapples - kolla family eller namn
  if (productTypeName.includes("grapple") || nameLower.includes("grip")) {
    // Railroad grapple redan hanterad ovan
    if (nameLower.includes("railroad") || nameLower.includes("järnväg") || nameLower.includes("rail")) {
      return localImages.jarnvagsgrip;
    }
    
    if (nameLower.includes("multi") || product.family?.toLowerCase().includes("multi")) {
      return localImages.MG;
    }
    if (nameLower.includes("stone") || nameLower.includes("sten") || product.family?.toLowerCase().includes("stone")) {
      return localImages.SG;
    }
    if (nameLower.includes("sorting") || nameLower.includes("sortering") || product.family?.toLowerCase().includes("sorting")) {
      return localImages.SOG;
    }
    // Fallback för andra grapples
    return localImages.multigrip;
  }
  
  // 🔹 Exakta matchningar för tiltrotatorer (R1, R2, RC1, etc.)
  // Måste vara R eller RC följt av siffror, men inte RR (railroad)
  const tiltrotatorMatch = product.name.match(/\b(RC\d+|R\d+)\b/i);
  if (tiltrotatorMatch && !nameLower.startsWith("rr")) {
    const model = tiltrotatorMatch[1].toUpperCase();
    if (localImages[model as keyof typeof localImages]) {
      return localImages[model as keyof typeof localImages];
    }
  }

  // 🔹 Machine fixings
  if (nameLower.includes("qc") || nameLower.includes("quick")) {
    return localImages.QC;
  }

  if (nameLower.includes("s-fixing") || nameLower.includes("sfaste") || nameLower.includes("maskinfaste")) {
    return localImages.MC_S;
  }

  if (nameLower.includes("tool holder") || nameLower.includes("redskapsinfastning")) {
    return localImages.redskapsinfastning;
  }

  // 🔹 Fallback: Normalisera och sök generellt i localImages (men INTE för buckets eller GB)
  const normalized = product.name
    .toLowerCase()
    .replace(/\s|-/g, "")
    .replace(/å/g, "a")
    .replace(/ä/g, "a")
    .replace(/ö/g, "o");

  // Exkludera "gb" från fallback-sökningen för att undvika konflikt med bucket-namn
  const matchedKey = Object.keys(localImages).find(key => {
    const keyLower = key.toLowerCase().replace(/_/g, "");
    // Skippa GB från generell matchning eftersom det orsakar konflikter
    if (key === "GB") return false;
    return normalized.includes(keyLower);
  });

  if (matchedKey) {
    return localImages[matchedKey as keyof typeof localImages];
  }

  // 🔹 Om ingen match hittas, returnera tom sträng
  return "";
};